"use client"

import { useState, useEffect, useMemo, useRef, useDeferredValue } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import {
  calculateWateringSchedule,
  type WateringCalibrationInput,
  type WateringScheduleResult,
} from "@/lib/watering-calculator"
import { handOffToResult } from "@/components/tools/shared/use-result-handoff"
import { useCalculatorAnalytics } from "@/hooks/useCalculatorAnalytics"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import {
  getRegionFromZipCode,
  REGION_DEFAULT_GRASS,
  toLegacyRegion,
} from "@/data/regional-climate"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { WateringPreview } from "@/components/tools/watering-preview"
import { RESTRICTIONS, type RestrictionId } from "@/lib/watering-schedule-days"
import { grassDisplayName } from "@/lib/grass-display"

// ── Tokens — site-matched neutral palette + brand-600 green ─────────
const W_BG = "#ffffff"
const W_INK = "#111827"
const W_INK2 = "#374151"
const W_RULE = "#e5e7eb"
const W_BRAND = "#16a34a"
const W_BRAND_700 = "#15803d"
const W_BRAND_50 = "#f0fdf4"
const W_BRAND_100 = "#dcfce7"
const W_PAPER_DEEP = "#f3f4f6" // gray-100 — faint backdrop behind the carded sections
const W_MUTED = "#6b7280"
const W_MUTED2 = "#9ca3af"
const W_FONT = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`

type Season = "spring" | "summer" | "fall" | "winter"
type GrassSeason = "warm" | "cool" | "trans"
type SoilId = "sand" | "loam" | "clay" | "silt"
type SystemId = "spray" | "rotor" | "hose" | "drip" | "manual"
type CalibrationMode = "estimate" | "average" | "per-zone"

// ── Mappings: form ids → engine inputs ────────────────────────────
const SOIL_TO_ENGINE: Record<SoilId, "clay" | "sandy" | "loamy" | "unknown"> = {
  sand: "sandy",
  loam: "loamy",
  clay: "clay",
  silt: "loamy",
}

const SOIL_ALIASES: Record<string, SoilId> = {
  sand: "sand",
  sandy: "sand",
  loam: "loam",
  loamy: "loam",
  clay: "clay",
  silt: "silt",
  silty: "silt",
}

function parseInitialSoil(soilType?: string): SoilId | null {
  if (!soilType) return null
  return SOIL_ALIASES[soilType.trim().toLowerCase()] ?? null
}

const SYSTEM_TO_ENGINE: Record<SystemId, string> = {
  spray: "Spray Heads",
  rotor: "Rotor Heads",
  hose: "Oscillating Sprinkler",
  drip: "Drip Irrigation",
  manual: "Hose and Sprayer",
}

// ── Display data for the picture cards ────────────────────────────
const SOILS: Array<{
  id: SoilId
  name: string
  hint: string
}> = [
  { id: "sand", name: "Sandy", hint: "Drains fast, so water more often, less per session" },
  { id: "loam", name: "Loamy", hint: "The ideal balance" },
  { id: "clay", name: "Clay", hint: "Holds water, so water deep, less often" },
  { id: "silt", name: "Silty", hint: "Smooth, holds moisture well" },
]

const SYSTEMS: Array<{
  id: SystemId
  name: string
  hint: string
}> = [
  { id: "spray", name: "Spray heads", hint: "Fast pop-up sprays" },
  { id: "rotor", name: "Rotor / MP heads", hint: "Slower rotating heads" },
  { id: "hose", name: "Hose-end sprinkler", hint: "Manual oscillator or impact" },
  { id: "drip", name: "Drip / soaker", hint: "Slow, deep, efficient" },
  { id: "manual", name: "By hand", hint: "Hose + spray nozzle" },
]

const SEASONS: Array<{ id: Season; name: string; hint: string }> = [
  { id: "spring", name: "Spring", hint: "cool, rain helps" },
  { id: "summer", name: "Summer", hint: "peak demand" },
  { id: "fall", name: "Fall", hint: "cooling off" },
  { id: "winter", name: "Winter", hint: "dormant" },
]

const GRASS_SEASONS: Array<{
  id: GrassSeason
  label: string
  note: string
  recommend: string
}> = [
  { id: "warm", label: "Warm-season", note: "SE, TX, S. CA", recommend: "Bermudagrass" },
  { id: "cool", label: "Cool-season", note: "NE, Midwest, PNW", recommend: "Tall Fescue" },
  { id: "trans", label: "Transition", note: "NC → MO → KS", recommend: "Zoysiagrass" },
]

const GRASSES_BY_SEASON: Record<GrassSeason, string[]> = {
  warm: ["Bermudagrass", "Zoysiagrass", "St. Augustinegrass", "Centipede", "Bahiagrass", "Kikuyugrass", "Carpetgrass"],
  cool: ["Tall Fescue", "Kentucky Bluegrass", "Perennial Ryegrass", "Fine Fescue", "Annual Ryegrass", "Mixed Cool-Season Blend"],
  trans: ["Zoysiagrass", "Tall Fescue", "Bermudagrass", "Kentucky Bluegrass"],
}

const REGION_TO_GRASS_SEASON: Record<string, GrassSeason> = {
  Southeast: "warm",
  "South Central": "warm",
  Northeast: "cool",
  "Great Lakes": "cool",
  "Upper Midwest": "cool",
  "Mountain West": "cool",
  "Mid-Atlantic": "trans",
  Central: "trans",
  "West Coast": "trans",
}

const SIZE_PRESETS: Array<{ value: number; label: string; note: string }> = [
  { value: 1500, label: "Small", note: "townhouse / patio" },
  { value: 5000, label: "Typical", note: "suburban lot" },
  { value: 10000, label: "Large", note: "¼-acre lot" },
  { value: 22000, label: "Half ac", note: "half-acre +" },
]

const ZONE_PRESETS = [1, 2, 4, 6, 8]
const MIN_ZONE_COUNT = 1
const MAX_ZONE_COUNT = 24
const CATCH_CAN_MINUTES_PRESETS = [10, 15, 20]
const MIN_CATCH_CAN_MINUTES = 5
const MAX_CATCH_CAN_MINUTES = 60

function clampZoneCount(value: number): number {
  if (!Number.isFinite(value)) return MIN_ZONE_COUNT
  return Math.max(MIN_ZONE_COUNT, Math.min(MAX_ZONE_COUNT, Math.round(value)))
}

function clampCatchCanMinutes(value: number): number {
  if (!Number.isFinite(value)) return 15
  return Math.max(MIN_CATCH_CAN_MINUTES, Math.min(MAX_CATCH_CAN_MINUTES, Math.round(value)))
}

function parsePositiveDecimal(value: string): number | undefined {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function formatRate(depth: string, minutes: number): string | null {
  const parsed = parsePositiveDecimal(depth)
  if (!parsed) return null
  return (parsed * (60 / minutes)).toFixed(2)
}

// ── Inline icons ──────────────────────────────────────────────────
function Ico({ size = 14, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}
const IcoDrop = (p: { size?: number }) => <Ico {...p}><path d="M12 3s7 7 7 12a7 7 0 11-14 0c0-5 7-12 7-12z" /></Ico>
const IcoLeaf = (p: { size?: number }) => <Ico {...p}><path d="M5 19c8 0 14-6 14-14C9 5 5 11 5 19z" /><path d="M5 19l9-9" /></Ico>
const IcoLawn = (p: { size?: number }) => <Ico {...p}><path d="M4 20h16" /><path d="M7 20c0-4 1-7 3-9" /><path d="M12 20c0-5 1-8 3-10" /><path d="M17 20c0-3 0-6 2-8" /></Ico>
const IcoLayers = (p: { size?: number }) => <Ico {...p}><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 13l9 5 9-5" /><path d="M3 18l9 5 9-5" /></Ico>
const IcoSun = (p: { size?: number }) => <Ico {...p}><circle cx="12" cy="12" r="4" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" /></Ico>
const IcoPin = (p: { size?: number }) => <Ico {...p}><path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" /><circle cx="12" cy="9" r="2.5" /></Ico>
const IcoBan = (p: { size?: number }) => <Ico {...p}><circle cx="12" cy="12" r="9" /><path d="M5 5l14 14" /></Ico>
const IcoChev = (p: { size?: number }) => <Ico {...p}><path d="M6 9l6 6 6-6" /></Ico>
const IcoEdit = (p: { size?: number }) => <Ico {...p}><path d="M4 20h4l10-10-4-4L4 16v4z" /><path d="M14 6l4 4" /></Ico>
const IcoLock = (p: { size?: number }) => <Ico {...p}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></Ico>
const IcoArrow = (p: { size?: number }) => <Ico {...p}><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></Ico>

// ── Soil + system glyphs (illustrated picture cards) ───────────────
function SoilGlyph({ id, active }: { id: SoilId; active: boolean }) {
  const ink = active ? W_BRAND : W_INK2
  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        background: active ? W_BRAND_100 : W_BRAND_50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {id === "sand" && (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <rect x="3" y="6" width="18" height="12" rx="1.5" />
          <circle cx="7" cy="10" r="0.8" fill={ink} />
          <circle cx="11" cy="13" r="0.8" fill={ink} />
          <circle cx="15" cy="10" r="0.8" fill={ink} />
          <circle cx="17" cy="14" r="0.8" fill={ink} />
          <circle cx="9" cy="14" r="0.8" fill={ink} />
          <circle cx="13" cy="9" r="0.8" fill={ink} />
          <path d="M12 2v3" />
        </svg>
      )}
      {id === "loam" && (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <rect x="3" y="6" width="18" height="12" rx="1.5" />
          <path d="M3 11h18M3 15h18" opacity="0.5" />
          <path d="M12 2v3" />
        </svg>
      )}
      {id === "clay" && (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <rect
            x="3"
            y="6"
            width="18"
            height="12"
            rx="1.5"
            fill={active ? "rgba(22,163,74,0.18)" : "rgba(17,24,39,0.06)"}
          />
          <path d="M12 2v3" />
          <path d="M11 5c-.5 1-1.5 1.5-1.5 1.5M13 5c.5 1 1.5 1.5 1.5 1.5" />
        </svg>
      )}
      {id === "silt" && (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <rect x="3" y="6" width="18" height="12" rx="1.5" />
          <path d="M3 9h18M3 12h18M3 15h18" opacity="0.4" />
          <path d="M12 2v3" />
        </svg>
      )}
    </span>
  )
}

function SystemGlyph({ id, active }: { id: SystemId; active: boolean }) {
  const ink = active ? W_BRAND : W_INK2
  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        background: active ? W_BRAND_100 : W_BRAND_50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {id === "spray" && (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <path d="M12 22v-9" />
          <circle cx="12" cy="13" r="1.5" fill={ink} />
          <path d="M12 13c-4-2-7-2-10-1M12 13c4-2 7-2 10-1M12 13c-3-2-4-5-3-9M12 13c3-2 4-5 3-9" />
        </svg>
      )}
      {id === "rotor" && (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <path d="M12 22v-9" />
          <circle cx="12" cy="13" r="1.5" fill={ink} />
          <path d="M12 13c-3-3-7-4-10-3M12 13c3-3 7-4 10-3M12 13c-2-3-3-6-2-9M12 13c2-3 3-6 2-9" />
        </svg>
      )}
      {id === "hose" && (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <path d="M3 20c4 0 4-6 8-6s4 6 8 6" />
          <circle cx="11" cy="14" r="1.2" fill={ink} />
          <path d="M11 12c2-2 5-3 8-3M13 10c2-1 4-1 6 0" />
        </svg>
      )}
      {id === "drip" && (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round">
          <path d="M3 12h18" />
          <circle cx="7" cy="12" r="0.7" fill={ink} />
          <circle cx="12" cy="12" r="0.7" fill={ink} />
          <circle cx="17" cy="12" r="0.7" fill={ink} />
          <path d="M7 14v5M12 14v5M17 14v5" opacity="0.5" />
        </svg>
      )}
      {id === "manual" && (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 20l4-12c1-3 4-3 5 0l4 12" />
          <path d="M19 14l3-2M19 16l3-1" opacity="0.7" />
        </svg>
      )}
    </span>
  )
}

// ── Section primitives ─────────────────────────────────────────────
function StepCircle({ step, accent }: { step: number; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 27,
        height: 27,
        flexShrink: 0,
        borderRadius: "50%",
        background: accent ? W_BRAND_50 : W_PAPER_DEEP,
        border: accent ? `1.5px solid ${W_BRAND}` : `1px solid ${W_RULE}`,
        boxShadow: accent ? `0 0 0 3px ${W_BRAND}22` : "none",
        color: accent ? W_BRAND_700 : W_INK,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  )
}

function Label({
  icon,
  children,
  hint,
  step,
  accent,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
  hint?: React.ReactNode
  step?: number
  accent?: boolean
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: 10,
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {step != null && <StepCircle step={step} accent={accent} />}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: W_BRAND_700,
          }}
        >
          {icon}
          {children}
        </div>
      </div>
      {hint && (
        <div style={{ fontSize: 13, color: W_MUTED, textAlign: "right" }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${W_RULE}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      {children}
    </div>
  )
}

// ── Lawn size ──────────────────────────────────────────────────────
function SizeField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))
  useEffect(() => {
    if (!editing) setDraft(String(value))
  }, [value, editing])

  const min = 200
  const max = 30000
  const toSlider = (v: number) =>
    Math.round(((Math.log(v) - Math.log(min)) / (Math.log(max) - Math.log(min))) * 1000)
  const fromSlider = (s: number) =>
    Math.round(Math.exp(Math.log(min) + (s / 1000) * (Math.log(max) - Math.log(min))) / 50) * 50

  return (
    <div>
      <Label step={3} icon={<IcoLawn size={13} />}>Lawn size</Label>

      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        {editing ? (
          <input
            autoFocus
            inputMode="numeric"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={() => {
              const n = Math.max(min, Math.min(max, parseInt(draft || "0", 10)))
              onChange(n)
              setEditing(false)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur()
            }}
            aria-label="Lawn size in square feet"
            style={{
              fontFamily: W_FONT,
              fontSize: 32,
              fontWeight: 700,
              color: W_INK,
              border: "none",
              borderBottom: `2px solid ${W_BRAND}`,
              background: "transparent",
              width: 180,
              padding: "2px 0",
              letterSpacing: "-0.025em",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Lawn size ${value} square feet, click to edit`}
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 8,
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "text",
              color: W_INK,
              textAlign: "left",
            }}
          >
            <span
              style={{
                fontFamily: W_FONT,
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: "-0.025em",
              }}
            >
              {value.toLocaleString()}
            </span>
            <span style={{ fontSize: 13, color: W_MUTED }}>sq ft</span>
            <span style={{ color: W_MUTED }}>
              <IcoEdit size={13} />
            </span>
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: W_MUTED }}>
          ≈ {(value / 43560).toFixed(value < 5000 ? 3 : 2)} ac
        </span>
      </div>

      <div style={{ position: "relative", padding: "6px 0 14px" }}>
        <input
          type="range"
          min={0}
          max={1000}
          value={toSlider(value)}
          onChange={(e) => onChange(fromSlider(parseInt(e.target.value, 10)))}
          aria-label="Lawn size slider"
          className="watering-range"
          style={{
            width: "100%",
            height: 6,
            WebkitAppearance: "none",
            appearance: "none",
            background: `linear-gradient(to right, ${W_BRAND} 0%, ${W_BRAND} ${toSlider(value) / 10}%, ${W_RULE} ${toSlider(value) / 10}%, ${W_RULE} 100%)`,
            borderRadius: 3,
            cursor: "pointer",
          }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {SIZE_PRESETS.map((p) => {
          const active = Math.abs(p.value - value) < 200
          return (
            <button
              type="button"
              key={p.value}
              onClick={() => onChange(p.value)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? W_BRAND_50 : "#fff",
                color: W_INK,
                border: `1px solid ${active ? W_BRAND : W_RULE}`,
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
                transition: "background-color 150ms var(--ease-standard), border-color 150ms var(--ease-standard)",
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.label}</div>
              <div style={{ fontSize: 11.5, color: W_MUTED, marginTop: 2 }}>
                {p.value.toLocaleString()} · {p.note}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Grass field ────────────────────────────────────────────────────
function GrassField({
  season,
  setSeason,
  grass,
  setGrass,
  zipKnown,
}: {
  season: GrassSeason
  setSeason: (s: GrassSeason) => void
  grass: string
  setGrass: (g: string) => void
  zipKnown: boolean
}) {
  const list = GRASSES_BY_SEASON[season]
  const active = GRASS_SEASONS.find((s) => s.id === season)!
  return (
    <div>
      <Label
        step={2}
        icon={<IcoLeaf size={13} />}
        hint={zipKnown ? `recommended for your area: ${active.recommend}` : undefined}
      >
        Grass type
      </Label>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          background: "#fff",
          border: `1px solid ${W_RULE}`,
          borderRadius: 8,
          padding: 4,
          gap: 4,
        }}
      >
        {GRASS_SEASONS.map((s) => {
          const isActive = s.id === season
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => {
                setSeason(s.id)
                // Keep the current grass if it's also valid in the new season
                // (e.g. Tall Fescue exists in both Cool and Transition); only
                // fall back to the recommendation when it isn't.
                if (!GRASSES_BY_SEASON[s.id].includes(grass)) {
                  setGrass(s.recommend)
                }
              }}
              aria-pressed={isActive}
              style={{
                padding: "10px 8px",
                background: isActive ? W_BRAND : "transparent",
                color: isActive ? "#fff" : W_INK,
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                textAlign: "center",
                transition: "background-color 200ms var(--ease-standard), color 200ms var(--ease-standard)",
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{s.label}</div>
              <div style={{ fontSize: 11, opacity: isActive ? 0.85 : 0.6, marginTop: 2 }}>
                {s.note}
              </div>
            </button>
          )
        })}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10, alignItems: "center" }}>
        {list.map((g) => {
          const isActive = g === grass
          return (
            <button
              type="button"
              key={g}
              onClick={() => setGrass(g)}
              aria-pressed={isActive}
              style={{
                padding: "7px 13px",
                background: isActive ? W_BRAND : "#fff",
                color: isActive ? "#fff" : W_INK2,
                border: `1px solid ${isActive ? W_BRAND : W_RULE}`,
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {grassDisplayName(g)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Soil field (illustrated picture cards) ─────────────────────────
function SoilField({ value, onChange }: { value: SoilId; onChange: (v: SoilId) => void }) {
  const blurb = SOILS.find((s) => s.id === value)?.hint
  return (
    <div>
      <Label step={4} icon={<IcoLayers size={13} />} hint={blurb}>Soil type</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {SOILS.map((s) => {
          const active = s.id === value
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => onChange(s.id)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? W_BRAND_50 : "#fff",
                color: W_INK,
                border: `1px solid ${active ? W_BRAND : W_RULE}`,
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
                transition: "background-color 150ms var(--ease-standard), border-color 150ms var(--ease-standard)",
              }}
            >
              <SoilGlyph id={s.id} active={active} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, overflowWrap: "break-word" }}>{s.name}</div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Irrigation system field (illustrated picture cards) ────────────
function SystemField({ value, onChange }: { value: SystemId; onChange: (v: SystemId) => void }) {
  const blurb = SYSTEMS.find((s) => s.id === value)?.hint
  return (
    <div>
      <Label step={5} icon={<IcoDrop size={13} />} hint={blurb}>Irrigation system</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {SYSTEMS.map((s) => {
          const active = s.id === value
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => onChange(s.id)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? W_BRAND_50 : "#fff",
                color: W_INK,
                border: `1px solid ${active ? W_BRAND : W_RULE}`,
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
                transition: "background-color 150ms var(--ease-standard), border-color 150ms var(--ease-standard)",
              }}
            >
              <SystemGlyph id={s.id} active={active} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, overflowWrap: "break-word" }}>{s.name}</div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Zone field ─────────────────────────────────────────────────────
function zoneFieldCopy(system: SystemId) {
  switch (system) {
    case "spray":
    case "rotor":
      return {
        label: "Controller zones",
        hint: "stations that run one at a time",
        note: "Use the number of sprinkler stations on your controller.",
      }
    case "hose":
      return {
        label: "Sprinkler positions",
        hint: "areas you move the sprinkler between",
        note: "Use the number of lawn sections you cover one at a time.",
      }
    case "drip":
      return {
        label: "Drip zones",
        hint: "valves or lines that run separately",
        note: "Use the number of drip zones that water separately.",
      }
    case "manual":
      return {
        label: "Watering areas",
        hint: "sections you water one at a time",
        note: "Use the number of areas you hand-water separately.",
      }
  }
}

function ZoneField({
  value,
  onChange,
  system,
  compact = false,
}: {
  value: number
  onChange: (v: number) => void
  system: SystemId
  compact?: boolean
}) {
  const copy = zoneFieldCopy(system)
  const update = (next: number) => onChange(clampZoneCount(next))

  return (
    <div>
      <Label icon={<IcoLayers size={13} />} hint={compact ? "one at a time" : copy.hint}>{copy.label}</Label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "40px minmax(0, 1fr) 40px" : "44px minmax(0, 1fr) 44px",
          gap: 8,
          alignItems: "stretch",
        }}
      >
        <button
          type="button"
          onClick={() => update(value - 1)}
          aria-label="Decrease zone count"
          disabled={value <= MIN_ZONE_COUNT}
          style={{
            minHeight: compact ? 40 : 44,
            border: `1px solid ${W_RULE}`,
            borderRadius: 8,
            background: "#fff",
            color: value <= MIN_ZONE_COUNT ? W_MUTED2 : W_INK,
            fontSize: 20,
            fontWeight: 600,
            cursor: value <= MIN_ZONE_COUNT ? "not-allowed" : "pointer",
          }}
        >
          -
        </button>
        <div
          style={{
            border: `1px solid ${W_RULE}`,
            borderRadius: 8,
            background: "#fff",
            padding: compact ? "7px 10px" : "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <input
            type="number"
            min={MIN_ZONE_COUNT}
            max={MAX_ZONE_COUNT}
            inputMode="numeric"
            value={value}
            onChange={(e) => update(parseInt(e.target.value || "1", 10))}
            aria-label={`${copy.label} count`}
            style={{
              width: 54,
              border: "none",
              outline: "none",
              fontSize: compact ? 22 : 24,
              fontWeight: 700,
              color: W_INK,
              fontFamily: W_FONT,
              background: "transparent",
            }}
          />
          <span style={{ fontSize: 13, color: W_MUTED, lineHeight: 1.35 }}>
            {value === 1 ? "single coverage area" : "zones that run separately"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => update(value + 1)}
          aria-label="Increase zone count"
          disabled={value >= MAX_ZONE_COUNT}
          style={{
            minHeight: compact ? 40 : 44,
            border: `1px solid ${W_RULE}`,
            borderRadius: 8,
            background: "#fff",
            color: value >= MAX_ZONE_COUNT ? W_MUTED2 : W_INK,
            fontSize: 20,
            fontWeight: 600,
            cursor: value >= MAX_ZONE_COUNT ? "not-allowed" : "pointer",
          }}
        >
          +
        </button>
      </div>
      {!compact && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {ZONE_PRESETS.map((preset) => {
            const active = preset === value
            return (
              <button
                type="button"
                key={preset}
                onClick={() => update(preset)}
                aria-pressed={active}
                style={{
                  minWidth: 44,
                  padding: "7px 10px",
                  background: active ? W_BRAND : "#fff",
                  color: active ? "#fff" : W_INK2,
                  border: `1px solid ${active ? W_BRAND : W_RULE}`,
                  borderRadius: 9999,
                  fontSize: 13,
                  fontWeight: 600,
                  minHeight: 44,
                  cursor: "pointer",
                }}
              >
                {preset}
              </button>
            )
          })}
        </div>
      )}
      <div
        style={{
          fontSize: 12,
          color: W_MUTED,
          marginTop: compact ? 6 : 8,
          display: "inline-flex",
          alignItems: "flex-start",
          gap: 6,
          lineHeight: 1.45,
        }}
      >
        <IcoLock size={11} />
        <span>
          {compact
            ? "Runtime is per zone; total controller time updates in the preview."
            : `${copy.note} Run time is per zone; the preview shows the full controller time if they run one after another.`}
        </span>
      </div>
    </div>
  )
}

// ── Season field ───────────────────────────────────────────────────
// Calibration field
function CalibrationField({
  mode,
  onModeChange,
  testMinutes,
  onTestMinutesChange,
  averageDepth,
  onAverageDepthChange,
  zoneCount,
  zoneDepths,
  onZoneDepthChange,
  compact = false,
}: {
  mode: CalibrationMode
  onModeChange: (mode: CalibrationMode) => void
  testMinutes: number
  onTestMinutesChange: (minutes: number) => void
  averageDepth: string
  onAverageDepthChange: (value: string) => void
  zoneCount: number
  zoneDepths: string[]
  onZoneDepthChange: (index: number, value: string) => void
  compact?: boolean
}) {
  const averageRate = formatRate(averageDepth, testMinutes)
  const measuredZoneCount = zoneDepths.filter((depth) => parsePositiveDecimal(depth)).length

  return (
    <div>
      <Label icon={<IcoDrop size={13} />} hint={compact ? "optional" : "optional catch-can test"}>
        Sprinkler output
      </Label>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 6,
          background: "#fff",
          border: `1px solid ${W_RULE}`,
          borderRadius: 8,
          padding: 4,
        }}
      >
        {[
          { id: "estimate" as const, label: "Estimate", hint: "system type" },
          { id: "average" as const, label: "Average", hint: "one can value" },
          { id: "per-zone" as const, label: "By zone", hint: "best accuracy" },
        ].map((option) => {
          const active = mode === option.id
          return (
            <button
              type="button"
              key={option.id}
              onClick={() => onModeChange(option.id)}
              aria-pressed={active}
              style={{
                padding: compact ? "8px 5px" : "10px 6px",
                background: active ? W_BRAND : "transparent",
                color: active ? "#fff" : W_INK,
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700 }}>{option.label}</div>
              {!compact && (
                <div style={{ fontSize: 10.5, opacity: active ? 0.85 : 0.6, marginTop: 2 }}>
                  {option.hint}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {mode === "estimate" ? (
        <p style={{ margin: "7px 0 0", color: W_MUTED, fontSize: 12, lineHeight: 1.45 }}>
          Using the system estimate. Switch modes after a catch-can test.
        </p>
      ) : (
        <div
          style={{
            marginTop: 10,
            border: `1px solid ${W_RULE}`,
            borderRadius: 10,
            background: "#fff",
            padding: compact ? 10 : 12,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: W_INK2, marginBottom: 6 }}>
                Test length
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {CATCH_CAN_MINUTES_PRESETS.map((preset) => {
                  const active = preset === testMinutes
                  return (
                    <button
                      type="button"
                      key={preset}
                      onClick={() => onTestMinutesChange(preset)}
                      aria-pressed={active}
                      style={{
                        padding: "7px 10px",
                        background: active ? W_BRAND : "#fff",
                        color: active ? "#fff" : W_INK2,
                        border: `1px solid ${active ? W_BRAND : W_RULE}`,
                        borderRadius: 9999,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {preset} min
                    </button>
                  )
                })}
                <input
                  type="number"
                  min={MIN_CATCH_CAN_MINUTES}
                  max={MAX_CATCH_CAN_MINUTES}
                  value={testMinutes}
                  onChange={(e) => onTestMinutesChange(clampCatchCanMinutes(parseInt(e.target.value || "15", 10)))}
                  aria-label="Catch-can test minutes"
                  style={{
                    width: 80,
                    border: `1px solid ${W_RULE}`,
                    borderRadius: 9999,
                    padding: "7px 10px",
                    fontSize: 16,
                    color: W_INK,
                    fontFamily: W_FONT,
                  }}
                />
              </div>
            </div>

            {mode === "average" ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: W_INK2, marginBottom: 6 }}>
                  Average water caught
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input
                    type="number"
                    min="0.01"
                    max="3"
                    step="0.01"
                    inputMode="decimal"
                    value={averageDepth}
                    onChange={(e) => onAverageDepthChange(e.target.value)}
                    placeholder="0.25"
                    aria-label="Average inches collected in catch cans"
                    style={{
                      width: 110,
                      border: `1px solid ${W_RULE}`,
                      borderRadius: 8,
                      padding: "10px 12px",
                      fontSize: 16,
                      fontWeight: 700,
                      color: W_INK,
                      fontFamily: W_FONT,
                    }}
                  />
                  <span style={{ fontSize: 13, color: W_MUTED }}>inches in {testMinutes} min</span>
                </div>
                <p style={{ margin: "8px 0 0", fontSize: 12, color: W_MUTED, lineHeight: 1.45 }}>
                  {averageRate
                    ? `That equals ${averageRate} inches per hour. We will use it for every zone.`
                    : "Place cans in the lawn, run one zone, then enter the average depth."}
                </p>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: W_INK2, marginBottom: 6 }}>
                  Water caught by zone
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(116px, 1fr))",
                    gap: 8,
                  }}
                >
                  {Array.from({ length: zoneCount }, (_, index) => {
                    const rate = formatRate(zoneDepths[index] ?? "", testMinutes)
                    return (
                      <label
                        key={index}
                        style={{
                          border: `1px solid ${W_RULE}`,
                          borderRadius: 8,
                          padding: "8px 9px",
                          background: "#fff",
                        }}
                      >
                        <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: W_INK2 }}>
                          Zone {index + 1}
                        </span>
                        <input
                          type="number"
                          min="0.01"
                          max="3"
                          step="0.01"
                          inputMode="decimal"
                          value={zoneDepths[index] ?? ""}
                          onChange={(e) => onZoneDepthChange(index, e.target.value)}
                          placeholder="0.25"
                          aria-label={`Zone ${index + 1} inches collected in catch cans`}
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            marginTop: 4,
                            fontSize: 17,
                            fontWeight: 700,
                            color: W_INK,
                            fontFamily: W_FONT,
                          }}
                        />
                        <span style={{ display: "block", fontSize: 11, color: W_MUTED }}>
                          {rate ? `${rate} in/hr` : `in ${testMinutes} min`}
                        </span>
                      </label>
                    )
                  })}
                </div>
                <p style={{ margin: "8px 0 0", fontSize: 12, color: W_MUTED, lineHeight: 1.45 }}>
                  {measuredZoneCount > 0
                    ? `${measuredZoneCount} of ${zoneCount} zones measured. Blank zones use the system estimate.`
                    : "Run each zone for the same test length and enter the average catch-can depth for that zone."}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function SeasonField({ value, onChange }: { value: Season; onChange: (s: Season) => void }) {
  return (
    <div>
      <Label step={6} icon={<IcoSun size={13} />}>Season</Label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          background: "#fff",
          border: `1px solid ${W_RULE}`,
          borderRadius: 8,
          padding: 4,
          gap: 4,
        }}
      >
        {SEASONS.map((s) => {
          const active = s.id === value
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => onChange(s.id)}
              aria-pressed={active}
              style={{
                padding: "10px 6px",
                background: active ? W_BRAND : "transparent",
                color: active ? "#fff" : W_INK,
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
              <div style={{ fontSize: 10.5, opacity: active ? 0.85 : 0.6, marginTop: 2 }}>
                {s.hint}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Restrictions chip strip ────────────────────────────────────────
function RestrictionField({
  value,
  onChange,
}: {
  value: RestrictionId
  onChange: (v: RestrictionId) => void
}) {
  return (
    <div>
      <Label step={7} icon={<IcoBan size={13} />}>Watering restrictions</Label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {RESTRICTIONS.map((r) => {
          const active = r.id === value
          return (
            <button
              type="button"
              key={r.id}
              onClick={() => onChange(r.id)}
              aria-pressed={active}
              style={{
                padding: "8px 14px",
                background: active ? W_BRAND : "#fff",
                color: active ? "#fff" : W_INK2,
                border: `1px solid ${active ? W_BRAND : W_RULE}`,
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {r.label}
            </button>
          )
        })}
      </div>
      <div
        style={{
          fontSize: 12,
          color: W_MUTED,
          marginTop: 8,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <IcoLock size={11} />
        Match what your city allows. We only schedule on permitted days.
      </div>
    </div>
  )
}

// ── Cost-per-gallon field ──────────────────────────────────────────
function CostField({
  value,
  onChange,
  defaultVal,
}: {
  value: number
  onChange: (v: number) => void
  defaultVal: number
}) {
  const [override, setOverride] = useState(false)
  return (
    <div>
      <Label step={8} hint={override ? "using your value" : "using local average"}>Water cost</Label>
      {!override ? (
        <button
          type="button"
          onClick={() => setOverride(true)}
          style={{
            width: "100%",
            padding: "12px 14px",
            background: "#fff",
            color: W_INK,
            border: `1px solid ${W_RULE}`,
            borderRadius: 6,
            textAlign: "left",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 14 }}>
            ${defaultVal.toFixed(3)} <span style={{ color: W_MUTED }}>/ gal</span>
          </span>
          <span style={{ fontSize: 13, color: W_BRAND, fontWeight: 600 }}>Set my own</span>
        </button>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 14, color: W_MUTED }}>$</span>
          <input
            inputMode="decimal"
            autoFocus
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
            style={{
              flex: 1,
              padding: "12px 14px",
              border: `1px solid ${W_BRAND}`,
              borderRadius: 6,
              background: "#fff",
              fontSize: 16,
              color: W_INK,
            }}
          />
          <span style={{ fontSize: 13, color: W_MUTED }}>/ gal</span>
          <button
            type="button"
            onClick={() => {
              onChange(defaultVal)
              setOverride(false)
            }}
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              fontSize: 13,
              color: W_MUTED,
              cursor: "pointer",
            }}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  )
}

// ── Form ───────────────────────────────────────────────────────────
const DEFAULT_COST_PER_GAL = 0.005

export interface WateringFormSubmitData {
  zipCode: string
  grassType: string
  lawnSize: number
  zoneCount: number
  calibrationMode: CalibrationMode
  catchCanMinutes?: number
  catchCanDepthInches?: number
  zoneCatchCanDepthsInches?: number[]
  soilType: "clay" | "sandy" | "loamy" | "unknown"
  irrigationSystem: string
  season: Season
  costPerGallon: number
  waterRestrictions: boolean
}

interface WateringFormProps {
  initialValues?: {
    zipCode?: string
    grassType?: string
    lawnSize?: number
    zoneCount?: number
    soilType?: string
    sunlight?: string
    season?: string
  }
  error?: string | null
  /** Called whenever the live calculated result changes, so the page can render
   * downstream UI (like the data chart) without duplicating the calculation. */
  onResultChange?: (result: WateringScheduleResult | null) => void
  onGrassChange?: (grass: string) => void
  /** Id of the page's result block, where the phone "See my week" button lands. */
  resultTargetId?: string
}

function CarpetgrassWateringUnavailable() {
  return (
    <div role="status" style={{ background: W_BG, border: `1px solid ${W_RULE}`, borderRadius: 12, padding: 20 }}>
      <h2 style={{ color: W_INK, fontSize: 18, fontWeight: 700, margin: "0 0 8px" }}>
        No verified watering amount for Carpetgrass
      </h2>
      <p style={{ color: W_INK2, fontSize: 14, lineHeight: 1.6, margin: "0 0 12px" }}>
        Let rainfall cover normal water needs. During drought, irrigate when the grass shows water stress. Avoid standing water.
      </p>
      <a href="/guides/carpetgrass#watering" style={{ color: W_BRAND_700, fontSize: 14, fontWeight: 600 }}>
        Read Carpetgrass watering guidance
      </a>
    </div>
  )
}

export function WateringForm({ initialValues, error, onResultChange, onGrassChange, resultTargetId }: WateringFormProps) {
  const searchParams = usePrerenderSafeSearchParams()

  // ── State (live, no submit gate) ─────────────────────────────────
  const [zip, setZip] = useState<string>("")
  const [grassSeason, setGrassSeason] = useState<GrassSeason>("cool")
  const [grass, setGrass] = useState<string>("Tall Fescue")
  const [sqft, setSqft] = useState<number>(5000)
  // The slider updates `sqft` on every input event. Everything downstream (the
  // schedule, the parent page, saved inputs) reads the deferred copy so a fast
  // drag cannot stack synchronous re-renders into React error #185.
  const deferredSqft = useDeferredValue(sqft)
  const [zoneCount, setZoneCount] = useState<number>(1)
  const [calibrationMode, setCalibrationMode] = useState<CalibrationMode>("estimate")
  const [catchCanMinutes, setCatchCanMinutes] = useState<number>(15)
  const [averageCatchCanDepth, setAverageCatchCanDepth] = useState<string>("")
  const [zoneCatchCanDepths, setZoneCatchCanDepths] = useState<string[]>([""])
  const [soil, setSoil] = useState<SoilId>("loam")
  const [system, setSystem] = useState<SystemId>("rotor")
  const [season, setSeason] = useState<Season>("summer")
  const [restriction, setRestriction] = useState<RestrictionId>("none")
  const [costPerGallon, setCostPerGallon] = useState<number>(DEFAULT_COST_PER_GAL)
  // Rainfall is fetched async per-ZIP. While we wait, liveResult uses 0 so
  // the preview renders immediately; once rainfall lands the schedule
  // re-renders with the rain offset (and the skip-this-week banner if
  // rainfall meets the weekly need).
  const [rainfallInches, setRainfallInches] = useState<number>(0)

  // ── Region resolution from ZIP ───────────────────────────────────
  const region = useMemo(() => getRegionFromZipCode(zip), [zip])
  const zipKnown = zip.length === 5 && region !== "Unknown"

  const { savedInputs, saveInputs } = useSavedInputs()
  const seededGrassFromSaved = useRef(false)
  const seededSizeFromSaved = useRef(false)
  const userEdited = useRef({ zip: false, grass: false, size: false, zone: false, soil: false, season: false, grassSeason: false })

  useEffect(() => {
    setZoneCatchCanDepths((previous) =>
      Array.from({ length: zoneCount }, (_, index) => previous[index] ?? "")
    )
  }, [zoneCount])

  useEffect(() => {
    if (initialValues?.zipCode && /^\d{5}$/.test(initialValues.zipCode) && !userEdited.current.zip) {
      setZip(initialValues.zipCode)
    }
    if (initialValues?.grassType && GRASS_CHARACTERISTICS[initialValues.grassType] && !userEdited.current.grass) {
      setGrass(initialValues.grassType)
      if (initialValues.grassType === "Carpetgrass") {
        userEdited.current.grassSeason = true
        setGrassSeason("warm")
      }
      seededGrassFromSaved.current = true
      seededFromZip.current = true
    }
    if (initialValues?.lawnSize && !userEdited.current.size) {
      setSqft(Math.max(200, Math.min(30000, initialValues.lawnSize)))
      seededSizeFromSaved.current = true
    }
    if (initialValues?.zoneCount && !userEdited.current.zone) {
      setZoneCount(clampZoneCount(initialValues.zoneCount))
    }
    const nextSoil = parseInitialSoil(initialValues?.soilType)
    if (nextSoil && !userEdited.current.soil) {
      setSoil(nextSoil)
    }
    if (
      initialValues?.season &&
      ["spring", "summer", "fall", "winter"].includes(initialValues.season) &&
      !userEdited.current.season
    ) {
      setSeason(initialValues.season as Season)
    }
  }, [initialValues])

  // Cross-site savedInputs takes precedence over zip-derived defaults.
  useEffect(() => {
    if (seededGrassFromSaved.current) return
    const saved = savedInputs.grassType
    if (saved && GRASS_CHARACTERISTICS[saved]) {
      setGrass(saved)
      if (saved === "Carpetgrass") {
        userEdited.current.grassSeason = true
        setGrassSeason("warm")
      }
      seededGrassFromSaved.current = true
      seededFromZip.current = true
    }
  }, [savedInputs.grassType])

  useEffect(() => {
    if (seededSizeFromSaved.current) return
    const saved = savedInputs.lawnSize
    if (typeof saved === 'number' && saved > 0) {
      setSqft(Math.max(200, Math.min(30000, saved)))
      seededSizeFromSaved.current = true
    }
  }, [savedInputs.lawnSize])

  // Auto-update grass season + recommended grass when region changes.
  const seededFromZip = useRef(false)
  useEffect(() => {
    if (!zipKnown) return
    // Only auto-pick the season from the ZIP region until the user has
    // manually chosen one. Without this guard the effect re-fires on every
    // grassSeason change and reverts the user's tab click straight back to
    // the region default, making Warm/Transition unselectable for cool ZIPs.
    if (!userEdited.current.grassSeason) {
      // REGION_TO_GRASS_SEASON is keyed on the legacy region set; degrade
      // fine-grained climate sub-regions (California, Florida, etc.) to parent.
      const next = REGION_TO_GRASS_SEASON[toLegacyRegion(region)]
      if (next && next !== grassSeason) {
        setGrassSeason(next)
        const recommended = GRASS_SEASONS.find((s) => s.id === next)?.recommend
        if (recommended) setGrass(recommended)
      }
    }
    if (!seededFromZip.current && !userEdited.current.grass) {
      const def = REGION_DEFAULT_GRASS[region]
      if (def && GRASS_CHARACTERISTICS[def]) setGrass(def)
      seededFromZip.current = true
    }
  }, [zipKnown, region, grassSeason])

  // Persist user-driven grass + size to localStorage so other tools prefill.
  // The `seeded*FromSaved` refs flip to true after the first read; once those
  // settle the value here is the user's, not a default.
  const grassWriteReady = useRef(false)
  useEffect(() => {
    onGrassChange?.(grass)
  }, [grass, onGrassChange])
  useEffect(() => {
    if (!grassWriteReady.current) {
      grassWriteReady.current = true
      return
    }
    if (grass && GRASS_CHARACTERISTICS[grass]) {
      saveInputs({ grassType: grass })
    }
  }, [grass, saveInputs])

  const sizeWriteReady = useRef(false)
  useEffect(() => {
    if (!sizeWriteReady.current) {
      sizeWriteReady.current = true
      return
    }
    if (deferredSqft > 0) saveInputs({ lawnSize: deferredSqft })
  }, [deferredSqft, saveInputs])

  const calibrationInput: WateringCalibrationInput | undefined = useMemo(() => {
    if (calibrationMode === "average") {
      const depth = parsePositiveDecimal(averageCatchCanDepth)
      return depth
        ? { testMinutes: catchCanMinutes, catchCanDepthInches: depth }
        : undefined
    }

    if (calibrationMode === "per-zone") {
      const depths = zoneCatchCanDepths.map((depth) => parsePositiveDecimal(depth))
      return depths.some((depth) => typeof depth === "number")
        ? { testMinutes: catchCanMinutes, zoneCatchCanDepthsInches: depths }
        : undefined
    }

    return undefined
  }, [averageCatchCanDepth, calibrationMode, catchCanMinutes, zoneCatchCanDepths])

  // ── Live calculation (re-runs on every state change, including the
  // rainfall fetch lower in this component) ───────────────────────
  const liveResult: WateringScheduleResult | null = useMemo(() => {
    if (!grass || grass === "Carpetgrass") return null
    try {
      return calculateWateringSchedule(
        grass,
        deferredSqft,
        SOIL_TO_ENGINE[soil],
        SYSTEM_TO_ENGINE[system],
        season,
        costPerGallon,
        restriction !== "none",
        rainfallInches,
        zoneCount,
        calibrationInput,
        initialValues?.sunlight ?? savedInputs.sunlight,
      )
    } catch {
      return null
    }
  }, [grass, deferredSqft, soil, system, season, costPerGallon, restriction, rainfallInches, zoneCount, calibrationInput, initialValues?.sunlight, savedInputs.sunlight])

  // Report only when the result changes. The callback sets parent state, so
  // keying the effect on its identity re-fires it on every parent render.
  const onResultChangeRef = useRef(onResultChange)
  useEffect(() => {
    onResultChangeRef.current = onResultChange
  })
  useEffect(() => {
    onResultChangeRef.current?.(liveResult)
  }, [liveResult])

  useCalculatorAnalytics(
    'watering',
    zipKnown && liveResult
      ? {
          inputs: {
            zip_code: zip,
            grass_type: grass,
            lawn_size_sqft: deferredSqft,
            zone_count: zoneCount,
            calibration_mode: calibrationMode,
            catch_can_minutes: calibrationMode === "estimate" ? undefined : catchCanMinutes,
            soil_type: soil,
            irrigation_system: system,
            season,
            cost_per_gallon: costPerGallon,
            water_restriction: restriction,
          },
          result: {
            weekly_water_inches: liveResult.weeklyWaterNeeded,
            soil_adjusted_inches: liveResult.soilAdjustedWater,
            weekly_gallons: liveResult.weeklyGallons,
            sessions_per_week: liveResult.sessionsPerWeek,
            session_duration_min: liveResult.sessionDuration,
            controller_run_minutes_per_session: liveResult.controllerRunMinutesPerSession,
            controller_run_minutes_per_week: liveResult.controllerRunMinutesPerWeek,
            application_rate_source: liveResult.applicationRateSource,
            application_rate_in_per_hour: liveResult.applicationRateInchesPerHour,
            skip_this_week: liveResult.skipThisWeek,
            rainfall_subtracted_in: liveResult.rainfallSubtracted,
            weekly_cost: liveResult.weeklyCost,
            monthly_cost: liveResult.monthlyCost,
          },
          location: zip,
          source: 'live_update',
        }
      : null,
  )

  // Fetch recent rainfall when ZIP becomes valid. The fetch is debounced
  // against the ZIP value so a user typing the digits one at a time does
  // not trigger four requests. When the request resolves, liveResult
  // re-renders with the rain offset applied — surfacing the
  // "skip this week" banner when rainfall already meets the weekly need.
  useEffect(() => {
    if (!zipKnown) {
      setRainfallInches(0)
      return
    }
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/rainfall?zip=${encodeURIComponent(zip)}`)
        if (!res.ok) {
          if (!cancelled) setRainfallInches(0)
          return
        }
        const data = await res.json()
        if (cancelled) return
        const inches = Number(data.totalInches)
        setRainfallInches(Number.isFinite(inches) && inches > 0 ? inches : 0)
      } catch {
        if (!cancelled) setRainfallInches(0)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [zip, zipKnown])

  // ── URL prefill (compatible with old shareable links) ────────────
  useEffect(() => {
    const sizeParam = searchParams.get("size")
    const grassParam = searchParams.get("grass")
    const zipParam = searchParams.get("zip")
    const zonesParam = searchParams.get("zones")
    const catchCanMinutesParam = searchParams.get("canMinutes")
    const catchCanDepthParam = searchParams.get("canDepth")
    const zoneDepthsParam = searchParams.get("zoneDepths")
    if (sizeParam) {
      const n = parseInt(sizeParam, 10)
      if (!Number.isNaN(n)) {
        userEdited.current.size = true
        setSqft(Math.max(200, Math.min(30000, n)))
      }
    }
    if (grassParam) {
      const normalized = grassParam.toLowerCase().replace(/-/g, " ")
      const matched = Object.keys(GRASS_CHARACTERISTICS).find(
        (n) => n.toLowerCase() === normalized,
      )
      if (matched) {
        userEdited.current.grass = true
        setGrass(matched)
        if (matched === "Carpetgrass") {
          userEdited.current.grassSeason = true
          setGrassSeason("warm")
        }
      }
    }
    if (zipParam && /^\d{5}$/.test(zipParam)) {
      userEdited.current.zip = true
      setZip(zipParam)
    }
    if (zonesParam) {
      const n = parseInt(zonesParam, 10)
      if (!Number.isNaN(n)) {
        userEdited.current.zone = true
        setZoneCount(clampZoneCount(n))
      }
    }
    if (catchCanMinutesParam) {
      const n = parseInt(catchCanMinutesParam, 10)
      if (!Number.isNaN(n)) setCatchCanMinutes(clampCatchCanMinutes(n))
    }
    if (zoneDepthsParam) {
      const depths = zoneDepthsParam
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
      if (depths.length > 0) {
        setCalibrationMode("per-zone")
        setZoneCount((current) => clampZoneCount(Math.max(current, depths.length)))
        setZoneCatchCanDepths(depths.slice(0, MAX_ZONE_COUNT))
      }
    } else if (catchCanDepthParam && parsePositiveDecimal(catchCanDepthParam)) {
      setCalibrationMode("average")
      setAverageCatchCanDepth(catchCanDepthParam)
    }
  }, [searchParams])

  // ── Mobile detection ─────────────────────────────────────────────
  const [narrow, setNarrow] = useState(false)
  const previewRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)")
    const sync = () => setNarrow(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  const wateringUnavailable = grass === "Carpetgrass"

  // The form is fully live — there is no submit gate. Wrapping in <form>
  // is kept so password-managers / browser autofill behave normally and
  // <fieldset>-style accessibility groupings stay intact.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const scheduleTuningControls = (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <ZoneField
        compact
        value={zoneCount}
        system={system}
        onChange={(next) => {
          userEdited.current.zone = true
          setZoneCount(next)
        }}
      />

      <CalibrationField
        compact
        mode={calibrationMode}
        onModeChange={setCalibrationMode}
        testMinutes={catchCanMinutes}
        onTestMinutesChange={(minutes) => setCatchCanMinutes(clampCatchCanMinutes(minutes))}
        averageDepth={averageCatchCanDepth}
        onAverageDepthChange={setAverageCatchCanDepth}
        zoneCount={zoneCount}
        zoneDepths={zoneCatchCanDepths}
        onZoneDepthChange={(index, value) => {
          setZoneCatchCanDepths((previous) => {
            const next = [...previous]
            next[index] = value
            return next
          })
        }}
      />
    </div>
  )

  return (
    <form
      onSubmit={handleSubmit}
      className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-elevation-2"
      style={{ color: W_INK, fontFamily: W_FONT }}
    >
      <style>{`
        .watering-range:focus-visible {
          outline: 3px solid color-mix(in srgb, ${W_BRAND} 45%, transparent);
          outline-offset: 4px;
        }
        .watering-range::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 22px; height: 22px; border-radius: 50%;
          background: #fff; border: 2px solid ${W_BRAND};
          box-shadow: 0 1px 4px rgba(0,0,0,0.15);
          cursor: grab;
        }
        .watering-range::-moz-range-thumb {
          width: 22px; height: 22px; border-radius: 50%;
          background: #fff; border: 2px solid ${W_BRAND};
          cursor: grab;
        }
      `}</style>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "minmax(0,1fr)" : "minmax(0,1fr) 400px",
          gap: narrow ? 24 : 32,
          padding: narrow ? "16px 12px 20px" : "32px 32px 36px",
        }}
      >
        {/* Mobile: live preview first */}
        {narrow && liveResult && (
          <div ref={previewRef} className="scroll-mt-20">
            <WateringPreview result={liveResult} restrictionId={restriction} compact />
          </div>
        )}
        {narrow && wateringUnavailable && <CarpetgrassWateringUnavailable />}

        {/* Form column — carded, numbered sequence on a faint deep-paper backdrop */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: narrow ? "transparent" : W_PAPER_DEEP,
            borderRadius: 14,
            padding: narrow ? 0 : 12,
          }}
        >
          <SectionCard>
            <div>
              <Label
                step={1}
                accent
                icon={<IcoPin size={13} />}
                hint={zipKnown ? `climate zone matched: ${region}` : "required, sets your local pricing"}
              >
                Where
              </Label>
              <ZipCodeInput
                value={zip}
                onChange={(next) => {
                  userEdited.current.zip = true
                  setZip(next)
                }}
                showLabel={false}
                enableGeolocation
                rememberLastZip
              />
            </div>
          </SectionCard>

          <SectionCard>
            <GrassField
              season={grassSeason}
              setSeason={(next) => {
                userEdited.current.grassSeason = true
                setGrassSeason(next)
              }}
              grass={grass}
              setGrass={(next) => {
                userEdited.current.grass = true
                setGrass(next)
              }}
              zipKnown={zipKnown}
            />
          </SectionCard>

          <SectionCard>
            <SizeField
              value={sqft}
              onChange={(next) => {
                userEdited.current.size = true
                setSqft(next)
              }}
            />
          </SectionCard>

          {!wateringUnavailable && <SectionCard>
            <SoilField
              value={soil}
              onChange={(next) => {
                userEdited.current.soil = true
                setSoil(next)
              }}
            />
          </SectionCard>}

          {!wateringUnavailable && <SectionCard>
            <SystemField value={system} onChange={setSystem} />
          </SectionCard>}

          {!wateringUnavailable && <SectionCard>
            <SeasonField
              value={season}
              onChange={(next) => {
                userEdited.current.season = true
                setSeason(next)
              }}
            />
          </SectionCard>}

          {!wateringUnavailable && <SectionCard>
            <RestrictionField value={restriction} onChange={setRestriction} />
          </SectionCard>}

          {!wateringUnavailable && <SectionCard>
            <CostField value={costPerGallon} onChange={setCostPerGallon} defaultVal={DEFAULT_COST_PER_GAL} />
          </SectionCard>}

          {error && (
            <div
              role="alert"
              style={{
                fontSize: 13,
                color: "#b04531",
                background: "#fff5f3",
                border: "1px solid #f5d4cd",
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Mobile: advanced schedule tuning (zones + calibration) below the numbered steps */}
        {narrow && !wateringUnavailable && (
          <div
            style={{
              borderTop: `1px solid ${W_RULE}`,
              padding: "20px 0 4px",
            }}
          >
            {scheduleTuningControls}
          </div>
        )}

        {/* Desktop: live preview right rail (sticky) */}
        {!narrow && liveResult && (
          <aside
            style={{
              alignSelf: "flex-start",
              position: "sticky",
              top: 24,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <WateringPreview result={liveResult} restrictionId={restriction} />
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-elevation-1">
              {scheduleTuningControls}
            </div>
            <div
              style={{
                fontSize: 12,
                color: W_MUTED,
                lineHeight: 1.55,
                display: "flex",
                gap: 8,
                padding: "0 4px",
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}>
                <IcoLock size={12} />
              </span>
              <span>
                Best at 6 AM: strong root growth, lowest evaporation. Numbers from
                university extension data, reviewed May 2026.
              </span>
            </div>
          </aside>
        )}
        {!narrow && wateringUnavailable && (
          <aside style={{ alignSelf: "flex-start", position: "sticky", top: 24 }}>
            <CarpetgrassWateringUnavailable />
          </aside>
        )}
      </div>

      {/* Mobile: week summary under the fields, linking down to the page's result */}
      {narrow && liveResult && !wateringUnavailable && (
        <div className="flex items-center gap-3 border-t border-gray-200 px-4 pt-4 pb-5">
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 text-[11px] font-bold uppercase tracking-wider text-green-700">
              {liveResult.skipThisWeek && liveResult.rainfallSubtracted > 0 ? "Skip this week" : "Your week"}
            </div>
            {liveResult.skipThisWeek ? (
              liveResult.rainfallSubtracted > 0 ? (
                <>
                  <div className="text-sm font-bold text-gray-900">0 gal needed</div>
                  <div className="mt-0.5 text-xs font-semibold leading-snug text-green-700">
                    {liveResult.rainfallSubtracted.toFixed(2)}″ of recent rain covers your need. Typical dry-week plan: {liveResult.weeklyGallons.toLocaleString()} gal.
                  </div>
                </>
              ) : (
                // Dormant: do NOT surface a watering schedule, it would
                // contradict the "don't water" advice.
                <div className="text-[13px] font-semibold leading-snug text-green-700">
                  No watering needed. {liveResult.grassType} is dormant this season.
                </div>
              )
            ) : (
              <div className="text-sm font-bold text-gray-900">
                {liveResult.weeklyGallons.toLocaleString()} gal
                {liveResult.zoneCount > 1 && <> · {liveResult.zoneCount} zones</>}
                {typeof liveResult.monthlyCost === "number" && liveResult.weeklyGallons > 0 && (
                  <> · ${liveResult.monthlyCost.toFixed(2)}/mo</>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              const target = (resultTargetId ? document.getElementById(resultTargetId) : null) ?? previewRef.current
              if (target) handOffToResult(target)
            }}
            className="press inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-full bg-action px-5 text-sm font-bold text-action-foreground hover:bg-action-hover"
          >
            See my week
          </button>
        </div>
      )}
    </form>
  )
}
